import React, { useCallback, useEffect, useRef } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import Select from '../../ui/Select';
import type { SelectLayoutProps } from './types';
import { useInlineEdit } from './useInlineEdit';

export function SelectLayout({
    value,
    displayValue,
    options,
    onSave,
    placeholder = 'Selecione...',
    canEdit = true,
    className = '',
    editTitle = 'Clique para editar',
}: Omit<SelectLayoutProps, 'layout'>) {
    const toDisplayString = useCallback((v: string) => v, []);
    const parse = useCallback((raw: string) => raw, []);

    const selectRef = useRef<HTMLSelectElement>(null);

    const edit = useInlineEdit({
        value,
        toDisplayString,
        parse,
        onCommit: () => {},
    });

    useEffect(() => {
        if (edit.isEditing) {
            selectRef.current?.focus();
        }
    }, [edit.isEditing]);

    const stopPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleSave = async (e: React.MouseEvent) => {
        stopPropagation(e);
        await onSave(edit.localValue);
        edit.endEditing();
    };

    const handleCancel = (e: React.MouseEvent) => {
        stopPropagation(e);
        edit.cancel();
    };

    const handleStart = (e: React.MouseEvent) => {
        stopPropagation(e);
        edit.startEditing();
    };

    if (!edit.isEditing) {
        return (
            <div
                role={canEdit ? 'button' : undefined}
                tabIndex={canEdit ? 0 : undefined}
                onClick={canEdit ? handleStart : undefined}
                onKeyDown={
                    canEdit
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                edit.startEditing();
                            }
                        }
                        : undefined
                }
                className={`transition-colors ${canEdit
                    ? 'cursor-pointer hover:text-primary flex items-center gap-2 group/edit'
                    : 'cursor-default'
                    } ${className || 'text-body-sm font-medium text-default'}`}
                title={canEdit ? editTitle : 'Valor definido automaticamente'}
            >
                {displayValue}
                {canEdit && (
                    <Pencil className="w-3.5 h-3.5 text-muted opacity-0 group-hover/edit:opacity-100 transition-opacity" />
                )}
            </div>
        );
    }

    return (
        <div
            className={`flex items-center gap-1 bg-surface p-1 rounded border border-border w-full max-w-xs ${className}`}
            onClick={stopPropagation}
        >
            <Select
                ref={selectRef}
                selectSize="sm"
                variant="filled"
                containerClassName="flex-1 min-w-0 !mb-0"
                className="!border-none !bg-transparent !shadow-none !py-1 !px-1 text-secondary font-medium"
                options={options}
                placeholder={placeholder}
                value={edit.localValue}
                onChange={(e) => edit.setLocalValue(e.target.value)}
            />
            <button
                type="button"
                onClick={handleSave}
                className="p-1 hover:bg-green-50 text-green-600 rounded flex-shrink-0"
                title="Salvar"
                aria-label="Salvar"
            >
                <Check className="w-4 h-4" />
            </button>
            <button
                type="button"
                onClick={handleCancel}
                className="p-1 hover:bg-red-50 text-red-600 rounded flex-shrink-0"
                title="Cancelar"
                aria-label="Cancelar"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
